import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import Heading from "@components/layout/Heading";
import Label from "@components/form/Label";
import Alert from "@components/shared/Alert";
import { useExtractionPointContext } from "@context/ExtractionPointContext";
import { useAllExtractionRights } from "@hooks/query/useAllExtractionRights";

/**
 * An offtake only becomes active once it is linked to an entitlement, so the
 * create workflow offers that link here instead of leaving a new offtake
 * inactive with no indication of what is missing.
 */
const ExtractionPointLinkToEntitlement: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { details, handleChangeDetails, stepHelpers, navigateForCancel } =
    useExtractionPointContext();

  const { data: options = [], isLoading } = useAllExtractionRights({
    params: {
      assertedByWalletId: details.subscriber?.walletId,
      isActive: true,
    },
    options: {
      enabled: Boolean(details.subscriber?.walletId),
      select: (data: any[]) =>
        data
          .filter((i: any) => i.level0ResourceId === details.level0wrs?.id)
          .map((i: any) => ({ value: i.id, label: i.name })),
    },
  });

  // Mirrors the Add entitlement workflow, which pre-selects the customer's
  // offtakes in the chosen zone: here the entitlements are pre-selected.
  useEffect(() => {
    if (isLoading || details.linkToExtractionRights.length) return;
    handleChangeDetails(
      "linkToExtractionRights",
      options.map((i: any) => ({ id: i.value, name: i.label })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, options.length]);

  const selected = options.filter((option: any) =>
    details.linkToExtractionRights.some((i: any) => i.id === option.value),
  );

  return (
    <form className="flex flex-col gap-6 p-6 grow justify-between">
      <div className="space-y-4">
        <Heading light>
          {t("extraction_point.create.link_to_entitlement")}
        </Heading>

        {/* Nothing is shown until the entitlements are in, so the selector does
            not flash up and then disappear when there are none to offer. */}
        {isLoading ? null : !options.length ? (
          <Alert type="info">
            {t("extraction_point.create.link_entitlement.no_entitlement", {
              level0ResourceIdentifier: details.level0wrs?.identifier,
            })}
          </Alert>
        ) : (
          <>
            <div className="w-96">
              <Label htmlFor="linkToExtractionRights">
                {t("common.extraction_rights")}
              </Label>
              <Select
                inputId="linkToExtractionRights"
                isMulti
                options={options}
                value={selected}
                onChange={(values: any) =>
                  handleChangeDetails(
                    "linkToExtractionRights",
                    values.map((i: any) => ({ id: i.value, name: i.label })),
                  )
                }
              />
            </div>

            <Alert type="info">
              {details.linkToExtractionRights.length
                ? t("extraction_point.create.link_entitlement.will_activate", {
                    extractionRightNames: details.linkToExtractionRights
                      .map((i: any) => i.name)
                      .join(", "),
                  })
                : t("extraction_point.create.link_entitlement.will_stay_inactive")}
            </Alert>
          </>
        )}
      </div>

      <footer className="flex gap-4 -mx-2 mt-6 p-6 pb-0 border-t border-gray-200">
        <button
          type="button"
          className="btn-outline-primary"
          onClick={stepHelpers.goToPrevStep}
        >
          {t("common.prev_step")}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={stepHelpers.goToNextStep}
        >
          {t("common.next_step")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={() => navigateForCancel()}
        >
          {t("common.cancel")}
        </button>
      </footer>
    </form>
  );
};

export default ExtractionPointLinkToEntitlement;
